/**
 * The signage screens a scope can plan for.
 *
 * One location's, or every location of a customer. That widening IS the
 * cross-premise feature: a media manager hired by a chain sees one list
 * covering the whole estate, with each screen tagged by where it stands.
 */
import { foyerApi, type SignageScopeRef } from "../http/foyer";
import { orientationOfResolution, type ScreenOrientation } from "@shared/media";

export interface SignageScreen {
  id: string;
  /** The location it stands in - assignment writes are premise-keyed. */
  premiseId: string;
  premiseName: string;
  name: string;
  resolution?: string;
  /**
   * How it is hung: its unit's orientation, else the resolution it reports.
   * Null = neither is known (no unit setting, never checked in).
   */
  orientation: ScreenOrientation | null;
}

export async function listSignageScreensIn(
  scope: SignageScopeRef
): Promise<SignageScreen[]> {
  // The global scope owns no screens: it is Nenda's content layer, not a place
  // anything is hung. Answering [] is the honest result - a channel author has
  // nothing to assign a day plan to, and never should have.
  if (scope.kind === "global") return [];

  const premises =
    scope.kind === "premise"
      ? [{ premiseId: scope.premiseId, premiseName: "" }]
      : (await foyerApi.listPremises())
          .filter((p) => p.companyId === scope.companyId)
          .map((p) => ({
            premiseId: p.premiseId,
            premiseName: p.premiseName,
          }));

  const perPremise = await Promise.all(
    premises.map(async (p) => {
      // One slow or missing location must not blank the whole estate.
      const list = await foyerApi.listScreens(p.premiseId).catch(() => []);
      // Only screens that ARE signage. A hotel's in-room TVs and a care unit's
      // door signs cannot run a lobby campaign, and listing them implies they
      // can - an operator would tick a bedroom TV and wonder why nothing ran.
      return list
        .filter((s) => s.role === "signage")
        .map((s) => ({
          id: s.id,
          premiseId: p.premiseId,
          premiseName: p.premiseName,
          name: s.roomId ? `${s.roomId} · ${s.model ?? ""}`.trim() : s.id,
          resolution: s.live?.resolution,
          // The unit's setting first (UNIT-2), else what the screen reports.
          orientation:
            s.unitOrientation ?? orientationOfResolution(s.live?.resolution),
        }));
    })
  );
  return perPremise.flat();
}
